import React, { useContext } from 'react';
import { Navigate } from 'react-router-dom';
import { LoadingOverlay } from '@mantine/core';
import { authContext } from '../context/AuthProvider.component';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'LCA-Admin' | 'user';
  fallbackPath?: string;
  showUnauthorized?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole = 'LCA-Admin', 
  fallbackPath = '/dashboard',
  showUnauthorized = false 
}) => {
  const { getUserRole, isAuthenticated, hasRole, auth }: any = useContext(authContext);
  const role = getUserRole(); // Use secure role from context

  // Don't redirect while still loading/verifying token
  if (auth.loading) {
    return (
      <LoadingOverlay
        visible={true}
        zIndex={1000}
        overlayProps={{
          radius: 'sm',
          blur: 2,
          style: {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%'
          }
        }}
        loaderProps={{ color: '#404040', type: 'bars' }}
      />
    );
  }

  // Check if user has required role using secure auth context
  const hasRequiredRole = () => {
    if (!isAuthenticated()) return false;
    
    if (requiredRole === 'LCA-Admin') {
      return hasRole('LCA-Admin');
    }
    
    if (requiredRole === 'user') {
      // Any authenticated user (including LCA-Admin) can access user routes
      return isAuthenticated();
    }
    
    return false;
  };

  // If user doesn't have required role
  if (!hasRequiredRole()) {
    if (showUnauthorized) {
      return (
        <div style={{ 
          padding: '2rem', 
          textAlign: 'center',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          margin: '2rem'
        }}>
          <h2 style={{ color: '#d32f2f', marginBottom: '1rem' }}>Access Denied</h2>
          <p style={{ marginBottom: '0.5rem' }}>You don't have permission to access this page.</p>
          <p style={{ color: '#666', fontSize: '0.9rem' }}>
            Required access: <strong>{requiredRole === 'LCA-Admin' ? 'LCA-Admin' : 'Regular User'}</strong>
          </p>
          <p style={{ color: '#666', fontSize: '0.9rem' }}>
            Your current group: <strong>{role || 'Not authenticated'}</strong>
          </p>
        </div>
      );
    }
    
    // Redirect to fallback path
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;